import React, { useState } from 'react';
import { FaTooth, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';

const DentoalveolarSurgeryForm = ({ dentoalveolarData, handleDentoalveolarChange, handleFormSubmission }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, type, checked } = event.target;
    const value = type === "checkbox" ? (checked ? "Yes" : "No") : event.target.value;
    handleDentoalveolarChange({
      target: {
        name,
        value
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      handleFormSubmission(e);
      setIsSubmitting(false);
    }, 100);
  };

  const complications = [
    { name: 'jawFracture', label: 'Jaw Fracture' },
    { name: 'alveolarOsteitis', label: 'Alveolar Osteitis' },
    { name: 'sinusExposure', label: 'Sinus Exposure' },
    { name: 'nerveDamage', label: 'Nerve Damage' },
    { name: 'retainedRootTip', label: 'Retained Root Tip' },
    { name: 'damageAdjacentTeeth', label: 'Damage to Adjacent Teeth' },
    { name: 'lipLaceration', label: 'Lip Laceration' },
    { name: 'lipBurn', label: 'Lip Burn' },
    { name: 'retainedInstrumentForeignBody', label: 'Retained Instrument / Foreign Body' },
    { name: 'infection', label: 'Infection' },
    { name: 'hemorrhage', label: 'Hemorrhage' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex size-16 items-center justify-center rounded-full bg-orange-100">
            <FaTooth className="size-8 text-orange-600" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Dentoalveolar Surgery Complication Form
          </h1>
          <p className="mt-2 text-base text-gray-600">
            Report complications from extractions and surgical procedures
          </p>
        </div>

        {/* Form Card */}
        <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5">
          <form onSubmit={handleSubmit}>
            <div className="space-y-12 px-6 py-8 sm:px-10 sm:py-10">
              {/* Complications Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaExclamationTriangle className="size-5 text-gray-400" />
                  Complications
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Select all complications that occurred during the procedure
                </p>

                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                  {complications.map((complication) => (
                    <div key={complication.name} className="flex gap-3">
                      <div className="flex h-6 shrink-0 items-center">
                        <div className="group grid size-4 grid-cols-1">
                          <input
                            type="checkbox"
                            name={complication.name}
                            checked={dentoalveolarData[complication.name] === "Yes"}
                            onChange={handleChange}
                            className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                          />
                          <svg
                            fill="none"
                            viewBox="0 0 14 14"
                            className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                          >
                            <path
                              d="M3 8L6 11L11 3.5"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="opacity-0 group-has-[:checked]:opacity-100"
                            />
                          </svg>
                        </div>
                      </div>
                      <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">
                        {complication.label}
                      </label>
                    </div>
                  ))}
                </div>

                {/* Nerve Damage Type - Conditional */}
                {dentoalveolarData.nerveDamage === "Yes" && (
                  <div className="mt-6">
                    <label htmlFor="nerveDamageType" className="block text-sm/6 font-medium text-gray-900">
                      Specify Nerve Damage Type
                    </label>
                    <div className="mt-2">
                      <input
                        id="nerveDamageType"
                        name="nerveDamageType"
                        type="text"
                        value={dentoalveolarData.nerveDamageType || ''}
                        onChange={handleDentoalveolarChange}
                        placeholder="Enter nerve damage type"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                )}

                {/* Hemorrhage Management - Conditional */}
                {dentoalveolarData.hemorrhage === "Yes" && (
                  <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="text-sm/6 font-medium text-gray-900 mb-3">
                      Hemorrhage management
                    </p>
                    <div className="space-y-3">
                      <div className="flex gap-3">
                        <input
                          type="checkbox"
                          id="hemorrhageLocalControl"
                          name="hemorrhageLocalControl"
                          checked={dentoalveolarData.hemorrhageLocalControl === "Yes"}
                          onChange={handleChange}
                          className="size-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <label htmlFor="hemorrhageLocalControl" className="text-sm text-gray-900">
                          Local control
                        </label>
                      </div>
                      <div className="flex gap-3">
                        <input
                          type="checkbox"
                          id="hemorrhageEmbolization"
                          name="hemorrhageEmbolization"
                          checked={dentoalveolarData.hemorrhageEmbolization === "Yes"}
                          onChange={handleChange}
                          className="size-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <label htmlFor="hemorrhageEmbolization" className="text-sm text-gray-900">
                          Embolization required
                        </label>
                      </div>
                      <div className="flex gap-3">
                        <input
                          type="checkbox"
                          id="hemorrhageHospitalization"
                          name="hemorrhageHospitalization"
                          checked={dentoalveolarData.hemorrhageHospitalization === "Yes"}
                          onChange={handleChange}
                          className="size-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <label htmlFor="hemorrhageHospitalization" className="text-sm text-gray-900">
                          Hospitalization / Operating room required
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-center gap-x-4 border-t border-gray-200 px-6 py-4 sm:px-10">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-0.5 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FaCheckCircle aria-hidden="true" className="-ml-0.5 size-5" />
                    <span>Submit Form</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DentoalveolarSurgeryForm;
